// Lazarus global search for platform components -- window.__PLATFORM__.globalSearch.
//
// For an app that draws its OWN Cmd+K palette (its theme, its key handling): the same results
// the host palette shows, to render however the app likes. An app without a palette sets
// app.yaml `configs: { global_search: true }` and gets the host one instead -- never both, or
// Cmd+K opens two palettes.
//
//   const { items, records } = await window.__PLATFORM__.globalSearch(query);
//
//   items:   [{ name, path, icon, colour, section }] -- this app's pages first (section = the
//            app name), then Lazarus reports, report pages, settings, other apps and orgs.
//            Case-insensitive substring match on name; "" matches everything.
//   records: [{ display, table_name, report_links: [{ label, url }] }] -- live record matches,
//            only for a query of 3+ characters where the member may search entities; else [].
//
// Never rejects: a section that fails comes back empty and is logged to the console (the
// server reports its own 500s). Debouncing and dropping stale responses are the caller's job,
// e.g. a `live` flag in the effect that calls this.
//
//   window.__PLATFORM__.openGlobalSearch() -- opens the HOST palette from the app's own chrome
//   (a custom rail has no host nav, so no Search button). Returns false when this app did not
//   mount one (configs.global_search off), so the caller can hide its trigger.

// The host palette (controllers/command_palette_controller.js) imports this threshold and the
// two fetchers below, so both palettes ask the server the same way.
export const RECORDS_MIN_QUERY = 3;
// The palette list is the whole searchable set, filtered here, so one fetch serves every
// keystroke. It expires so a long-lived workspace page (no Turbo visits) still picks up
// changed reports and grants -- the host palette refetches on every open.
const ITEMS_TTL_MS = 60 * 1000;
// Must match HomeHelper::PLATFORM_GLOBAL_SEARCH_EVENT (the host palette root listens for it).
const OPEN_EVENT = "platform:global-search";

// "<org>/<app>" -> { list: Promise<items[]>, at }. A failed fetch is evicted so the next call retries.
const itemLists = new Map();

function meta(name) {
  return document.querySelector(`meta[name="${name}"]`)?.content;
}

async function fetchList(url) {
  const res = await fetch(url, { headers: { Accept: "application/json" } });
  if (!res.ok) throw new Error(`HTTP ${res.status} from ${url.split("?")[0]}`);
  const list = await res.json(); // a sign-in redirect lands on HTML and throws here
  if (!Array.isArray(list)) throw new Error(`unexpected payload from ${url.split("?")[0]}`);
  return list;
}

// The palette's whole searchable set; with `app`, that platform app's pages lead it.
export function fetchPaletteItems(org, app) {
  const params = app ? `?${new URLSearchParams({ platform_slug: app })}` : "";
  return fetchList(`/${org}/command_palette${params}`);
}

export function searchRecords(org, term) {
  return fetchList(`/${org}/entities/search?${new URLSearchParams({ q: term })}`);
}

function paletteItems(org, app) {
  const key = `${org}/${app || ""}`;
  const cached = itemLists.get(key);
  if (cached && Date.now() - cached.at < ITEMS_TTL_MS) return cached.list;

  const entry = { list: fetchPaletteItems(org, app), at: Date.now() };
  entry.list.catch(() => { if (itemLists.get(key) === entry) itemLists.delete(key); });
  itemLists.set(key, entry);
  return entry.list;
}

async function orEmpty(section, promise) {
  try {
    return await promise;
  } catch (error) {
    console.warn(`[platform] globalSearch: ${section} unavailable`, error);
    return [];
  }
}

// Whether this page mounted the host palette (configs.global_search on) -- the kit GlobalSearch
// field renders nothing without it.
export function globalSearchAvailable() {
  return !!document.querySelector("[data-command-palette-platform-slug-value]");
}

export function openGlobalSearch() {
  if (!globalSearchAvailable()) return false;
  window.dispatchEvent(new CustomEvent(OPEN_EVENT));
  return true;
}

export default async function globalSearch(query = "") {
  const org = meta("organizationSlug");
  // Portal contacts have no Lazarus session -- nothing here is theirs to search.
  if (!org || meta("platformPortal")) return { items: [], records: [] };

  const term = String(query ?? "").trim().toLowerCase();
  // entities/search answers a member without the permission with a redirect AND a
  // "not authorized" flash on their next page, so it is never called unless the page says so.
  const recordsAllowed = term.length >= RECORDS_MIN_QUERY && meta("platformEntitySearch") === "true";

  const [items, records] = await Promise.all([
    orEmpty("items", paletteItems(org, meta("platformAppSlug"))),
    recordsAllowed ? orEmpty("records", searchRecords(org, term)) : [],
  ]);
  return { items: items.filter((item) => String(item?.name ?? "").toLowerCase().includes(term)), records };
}
